/** "Watch the replay": a live's replay in the watch page's own player, in a sheet, from the start or a moment. */
import { useEffect, useMemo, useRef, useState } from "react";
import type { VideoStatusResponse, WatchChapter, WatchSession } from "../../../shared/types";
import { Button } from "../../components/ui/Button";
import { Sheet } from "../../components/ui/Sheet";
import { api } from "../../lib/api";
import { type Arrivals, Player } from "../../public/TalkPlayer";
import { clock } from "./liveWords";

const STILL: Arrivals = { up: null, waiting: 0, paused: false, onTime: () => undefined, dismiss: () => undefined };
const quiet = () => undefined;

/** The replay as the watch page's player needs it, starting where the host asked. */
function replaySession(webinarId: string, title: string, video: VideoStatusResponse, startSec: number, chapters: WatchChapter[]): WatchSession {
  return {
    registrant_id: "",
    webinar_id: webinarId,
    slug: "",
    title,
    host_name: "",
    host_avatar_url: null,
    duration_sec: video.duration_sec,
    playback_url: video.hls_url,
    media_token: video.media_token ?? null,
    poster_url: null,
    watched_sec: startSec,
    watched_pct: 0,
    offer: null,
    interactions: [],
    chapters,
    posts: [],
    messages: [],
    last_visit_at: 0,
  };
}

export function ReplaySheet({
  webinarId,
  sessionId,
  title,
  startSec = 0,
  chapters = [],
  onClose,
}: {
  webinarId: string;
  /** The live session whose own recording plays, by the check its "Ready" reads; the room's own video when absent. */
  sessionId?: string;
  title: string;
  startSec?: number;
  chapters?: WatchChapter[];
  onClose: () => void;
}) {
  const [video, setVideo] = useState<VideoStatusResponse | "failed" | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    api.webinars
      .videoStatus(webinarId, sessionId)
      .then((v) => alive && setVideo(v))
      .catch(() => alive && setVideo("failed"));
    return () => {
      alive = false;
    };
  }, [webinarId, sessionId]);

  const session = useMemo(
    () => (video && video !== "failed" && video.hls_url ? replaySession(webinarId, title, video, startSec, chapters) : null),
    [video, webinarId, title, startSec, chapters],
  );

  // It plays at once: the player moves to the saved place first, then this starts it.
  useEffect(() => {
    const el = box.current?.querySelector("video");
    if (!session || !el) return;
    const go = () => void el.play().catch(() => undefined);
    const when = startSec > 0 ? "seeked" : "canplay";
    el.addEventListener(when, go, { once: true });
    return () => el.removeEventListener(when, go);
  }, [session, startSec]);

  return (
    <Sheet open wide onClose={onClose} title={title} sub={startSec > 0 ? `Playing from ${clock(startSec)}` : undefined} footer={<Button onClick={onClose}>Close</Button>}>
      <div className="replaybox" ref={box}>
        {session ? (
          <Player session={session} onProgress={quiet} arrivals={STILL} onAnswer={quiet} chrome="watch" />
        ) : (
          <div className="replaywait small muted">{video === null ? "Getting the replay…" : "The replay isn't ready to play yet."}</div>
        )}
      </div>
    </Sheet>
  );
}
